import { CompositeLayer } from '@deck.gl/core'
import { LineLayer, ScatterplotLayer } from '@deck.gl/layers'

const nop = () => {}

const defaultProps = {
  ...CompositeLayer.defaultProps,
  groupKey: 'group',
  widthRange: [2, 3, 4, 5, 6],
  radiusRange: [8, 16, 24, 32, 40],
  getDurationValue: { type: 'accessor', value: (d) => d.duration },
  getCountValue: { type: 'accessor', value: (d) => d.count },
  onSetRadiusDomain: nop,
  onSetWidthDomain: nop,
}

/**
 * props._subLayerPropsから対象のvisibleを取得する
 * 未定義の場合はtrueを返す
 * @param {Object} props
 * @param {String} key subLayerのキー
 * @returns {Boolean}
 */
const getVisibleFromSubLayerProps = (props, key) => {
  const targetSubLayerProps = props._subLayerProps && props._subLayerProps[key]
  if (!targetSubLayerProps) return true
  return targetSubLayerProps.visible != null
    ? targetSubLayerProps.visible
    : true
}

/**
 * 動線強調マップ用カスタムレイヤー
 */
class FlowlineCompositeLayer extends CompositeLayer {
  updateState({ props, oldProps, context, changeFlags }) {
    super.updateState({ props, oldProps, context, changeFlags })
    const { dataChanged, propsChanged } = changeFlags
    if (dataChanged) {
      this._setRadiusDomain()
      this._setWidthDomain()
      this.setNeedsRedraw()
    } else if (propsChanged) {
      if (props.radiusRange !== oldProps.radiusRange) {
        this._setRadiusDomain()
        this.setNeedsRedraw()
      } else if (props.widthRange !== oldProps.widthRange) {
        this._setWidthDomain()
        this.setNeedsRedraw()
      }
    }
  }

  _setRadiusDomain() {
    const { data, radiusRange, onSetRadiusDomain } = this.props
    const durations = data.map((d) => d.stays.map((s) => s.duration)).flat()
    const domain = this._createDomain(durations, radiusRange)
    this.setState({ radiusDomain: domain })
    onSetRadiusDomain(domain)
  }

  _setWidthDomain() {
    const { data, widthRange, onSetWidthDomain } = this.props
    const counts = data.map((d) => d.flows.map((s) => s.count)).flat()
    const domain = this._createDomain(counts, widthRange)
    this.setState({ widthDomain: domain })
    onSetWidthDomain(domain)
  }

  _createDomain(data, range) {
    if (!data.length) return []
    const max = Math.max(...data)
    const maxSize = range[range.length - 1]
    const unit = max / maxSize
    return [...new Set(range.map((s) => Math.ceil(unit * s)))]
  }

  _getRadius(d) {
    // v9対応: getDurationValue が関数か定数かを判定して呼び出す (getColor is not a function 対策)
    const { getDurationValue } = this.props
    const value =
      typeof getDurationValue === 'function'
        ? getDurationValue(d)
        : getDurationValue
    for (let i = 0; i < this.state.radiusDomain.length; ++i) {
      if (value > this.state.radiusDomain[i]) continue
      return this.props.radiusRange[i]
    }
    return 0
  }

  _getWidth(d) {
    // v9対応: getCountValue が関数か定数かを判定して呼び出す (getColor is not a function 対策)
    const { getCountValue } = this.props
    const count =
      typeof getCountValue === 'function' ? getCountValue(d) : getCountValue
    for (let i = 0; i < this.state.widthDomain.length; ++i) {
      if (count > this.state.widthDomain[i]) continue
      return this.props.widthRange[i]
    }
    return 0
  }

  renderLayers() {
    const flowlineVisible = getVisibleFromSubLayerProps(this.props, 'flowline')
    const staycircleVisible = getVisibleFromSubLayerProps(
      this.props,
      'staycircle'
    )

    const layers = this.props.data
      .sort((a, b) => {
        // ハイライト対象を最後に描画するためのソート
        const { highlightedUserId, groupKey } = this.props
        if (highlightedUserId === null) {
          // ハイライト対象指定がない場合はuser_idの昇順ソート
          return a[groupKey] - b[groupKey]
        }
        const aIsHighlighted = a[groupKey] === highlightedUserId
        const bIsHighlighted = b[groupKey] === highlightedUserId
        if (aIsHighlighted) return 1
        if (bIsHighlighted) return -1
        return a[groupKey] - b[groupKey]
      })
      .map((d) => {
        const groupId = d[this.props.groupKey]
        return [
          new LineLayer(
            this.props,
            this.props._subLayerProps && this.props._subLayerProps.flowline,
            this.getSubLayerProps({
              id: `flowline-${groupId}`,
              data: d.flows,
              opacity: 0.8,
              widthUnits: 'pixels',
              getWidth: this._getWidth.bind(this),
              parameters: {
                blend: true,
                depthTest: true,
                depthRange: [0, 0.89],
                // v9対応: GL定数(数値)から文字列値へ置換 ('less')
                depthFunc: 'less',
              },
              updateTriggers: {
                // AppAnalyzeDataLayerManagerから渡されるupdateTriggersを展開し、マージする
                ...(this.props._subLayerProps?.flowline?.updateTriggers || {}),
                getWidth: [this.props.highlightedUserId],
              },
            }),
            // getSubLayerPropsによってvisibleが親レイヤーの値に上書きされるので事前に取得しておいて更に上書きする
            { visible: this.props.visible && flowlineVisible }
          ),
          new ScatterplotLayer(
            this.props,
            this.props._subLayerProps && this.props._subLayerProps.staycircle,
            this.getSubLayerProps({
              id: `staycircle-${groupId}`,
              data: d.stays,
              getPolygonOffset: ({ layerIndex }) => [
                0,
                -(layerIndex - 1) * 100,
              ],
              opacity: 0.8,
              radiusUnits: 'pixels',
              getRadius: this._getRadius.bind(this),
              parameters: {
                blend: true,
                depthTest: true,
                depthRange: [0, 0.89],
                // v9対応: GL定数(数値)から文字列値へ置換 ('less')
                depthFunc: 'less',
              },
              updateTriggers: {
                // AppAnalyzeDataLayerManagerから渡されるupdateTriggersを展開し、マージする
                ...(this.props._subLayerProps?.staycircle?.updateTriggers ||
                  {}),
                getRadius: [this.props.highlightedUserId],
              },
            }),
            // getSubLayerPropsによってvisibleが親レイヤーの値に上書きされるので事前に取得しておいて更に上書きする
            { visible: this.props.visible && staycircleVisible }
          ),
        ]
      })
    return layers
  }
}

FlowlineCompositeLayer.layerName = 'FlowlineCompositeLayer'
FlowlineCompositeLayer.defaultProps = defaultProps

export { FlowlineCompositeLayer }
